import { useState } from 'react'

import { api } from '../../api/client'
import type { MaintenanceWindow } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner } from '../common'
import { DataTable } from '../DataTable'
import { chipClass } from './labels'

function dayNames(): string[] {
  return [t('Lun'), t('Mar'), t('Mer'), t('Gio'), t('Ven'), t('Sab'), t('Dom')]
}

export function describeRecurrence(r: Record<string, unknown>): string {
  if (r.at) return t('una volta')
  const days = (r.weekdays as number[] | undefined) ?? []
  const names = dayNames()
  const d = days.length === 7 || days.length === 0 ? t('ogni giorno') : days.map((i) => names[i]).join(' ')
  return `${d} ${String(r.start ?? '')} (${Number(r.duration_min ?? 0)} min)`
}

/** The maintenance windows: when scheduled jobs are allowed to run. */
export function MaintenanceWindows({ canWrite }: { canWrite: boolean }) {
  const state = useAsync(() => api.patchWindows(), [])
  const [error, setError] = useState<unknown>(null)
  const empty: MaintenanceWindow = {
    name: '', enabled: true, timezone: 'Europe/Rome',
    recurrence: { weekdays: [5], start: '22:00', duration_min: 240 }, scope: { tags: [] },
  }
  const [draft, setDraft] = useState<MaintenanceWindow | null>(null)

  async function save() {
    if (!draft) return
    setError(null)
    try {
      if (draft.id) await api.patchWindowUpdate(draft.id, draft)
      else await api.patchWindowCreate(draft)
      setDraft(null)
      await state.reload()
    } catch (e) {
      setError(e)
    }
  }

  if (state.loading && !state.data) return <Spinner />
  const rows = state.data ?? []
  const days = (draft?.recurrence.weekdays as number[] | undefined) ?? []
  return (
    <Card title={t('Finestre di manutenzione')}
      actions={canWrite ? <button onClick={() => setDraft({ ...empty })}>{t('Nuova finestra')}</button> : undefined}>
      <ErrorBanner error={error} />
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Giorni e orari in cui un intervento programmato «in finestra» può partire. Fuori dalla finestra aspetta la prossima apertura.')}
      </p>
      {rows.length === 0 ? (
        !draft && <EmptyState title={t('Nessuna finestra definita.')} hint={t('Esempio: «Sabato notte», sabato dalle 22:00 per 4 ore.')} />
      ) : (
        <DataTable
          rows={rows}
          rowKey={(w, i) => w.id ?? String(i)}
          conteggio={{ nome: t('finestre') }}
          columns={[
            { key: 'name', header: t('Nome'), render: (w) => <strong>{w.name}</strong> },
            { key: 'when', header: t('Quando'), value: (w) => describeRecurrence(w.recurrence) },
            { key: 'timezone', header: t('Fuso') },
            {
              key: 'enabled', header: t('Stato'), value: (w) => (w.enabled ? t('attiva') : t('disattivata')),
              render: (w) => (w.enabled
                ? <span className={chipClass('ok')}>{t('attiva')}</span>
                : <span className={chipClass('muted')}>{t('disattivata')}</span>),
            },
            {
              key: 'actions', header: '', sort: false, filter: false, searchable: false,
              render: (w) => canWrite && (
                <span className="rowactions">
                  <button className="link" onClick={() => setDraft({ ...w })}>{t('Modifica')}</button>
                  <button className="link" onClick={async () => {
                    if (!window.confirm(t('Eliminare la finestra «{n}»?', { n: w.name }))) return
                    setError(null)
                    try { await api.patchWindowDelete(w.id!); await state.reload() } catch (e) { setError(e) }
                  }}>{t('Elimina')}</button>
                </span>
              ),
            },
          ]}
        />
      )}
      {draft && (
        <div className="patch-window-form">
          <div className="field">
            <label htmlFor="mw-name">{t('Nome')}</label>
            <input id="mw-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('es. Sabato notte')} />
          </div>
          <div className="field">
            <label>{t('Giorni')}</label>
            <div className="patch-days">
              {dayNames().map((g, i) => (
                <label key={i}>
                  <input type="checkbox" checked={days.includes(i)} onChange={(e) => {
                    const next = e.target.checked ? [...days, i].sort() : days.filter((x) => x !== i)
                    setDraft({ ...draft, recurrence: { ...draft.recurrence, weekdays: next } })
                  }} /> {g}
                </label>
              ))}
            </div>
          </div>
          <div className="patch-inline-fields">
            <div className="field">
              <label htmlFor="mw-start">{t('Dalle')}</label>
              <input id="mw-start" type="time" value={String(draft.recurrence.start ?? '')}
                onChange={(e) => setDraft({ ...draft, recurrence: { ...draft.recurrence, start: e.target.value } })} />
            </div>
            <div className="field">
              <label htmlFor="mw-dur">{t('Durata (minuti)')}</label>
              <input id="mw-dur" type="number" min={15} step={15} value={Number(draft.recurrence.duration_min ?? 120)}
                onChange={(e) => setDraft({ ...draft, recurrence: { ...draft.recurrence, duration_min: Number(e.target.value) } })} />
            </div>
            <div className="field">
              <label htmlFor="mw-tz">{t('Fuso')}</label>
              <input id="mw-tz" value={draft.timezone} onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} />
            </div>
          </div>
          <label>
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />{' '}
            {t('attiva')}
          </label>
          <div className="rowactions" style={{ marginTop: 8 }}>
            <button disabled={!draft.name.trim() || days.length === 0} onClick={save}>{t('Salva')}</button>
            <button className="secondary" onClick={() => setDraft(null)}>{t('Annulla')}</button>
          </div>
        </div>
      )}
    </Card>
  )
}
